import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Package, Plus, Trash2, Utensils, Home, Search, AlertTriangle } from "lucide-react";
import DestCityFilter from "../components/DestCityFilter";

const CATS = [
  { key: "guest_house", label: "Guest House", icon: Home },
  { key: "kitchen", label: "Kitchen", icon: Utensils },
];
const CONDITIONS = [
  { v: "good", label: "Good", cls: "text-emerald-700 border-emerald-500" },
  { v: "needs_repair", label: "Needs repair", cls: "text-amber-700 border-amber-500" },
  { v: "replace", label: "Replace", cls: "text-red-700 border-red-500" },
];

export default function Inventory() {
  const [cat, setCat] = useState("guest_house");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", units: 0, condition: "good" });
  const [filter, setFilter] = useState({ destination: "", city: "" });

  const load = async () => {
    const q = new URLSearchParams({ category: cat });
    if (filter.destination) q.set("destination", filter.destination);
    if (filter.city) q.set("city", filter.city);
    const { data } = await api.get(`/inventory?${q.toString()}`);
    setRows(data);
  };
  useEffect(() => { load(); }, [cat, filter.destination, filter.city]);

  const filtered = useMemo(
    () => rows.filter(r => r.name.toLowerCase().includes(q.toLowerCase())),
    [rows, q]
  );

  const totalUnits = useMemo(() => rows.reduce((s, r) => s + (r.units || 0), 0), [rows]);
  const flagged = useMemo(() => rows.filter(r => r.condition && r.condition !== "good").length, [rows]);
  const lowStock = useMemo(
    () => rows.filter(r => Number(r.units || 0) < Number(r.threshold ?? 2)),
    [rows]
  );

  const add = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    await api.post("/inventory", { ...form, category: cat, units: parseInt(form.units) || 0 });
    setForm({ name: "", units: 0, condition: "good" });
    setAdding(false);
    toast.success("Added");
    await load();
  };

  const patch = async (id, body) => {
    await api.patch(`/inventory/${id}`, body);
    await load();
  };

  const del = async (r) => {
    if (!window.confirm(`Remove "${r.name}" from inventory?`)) return;
    await api.delete(`/inventory/${r.id}`);
    await load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Guest Experience</div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Package className="text-brand" /> Inventory</h1>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Track every item in the guest house and kitchen. Flag anything that needs repair or replacement — BDOM gets notified.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DestCityFilter value={filter} onChange={setFilter} />
          <button data-testid="inv-add" onClick={()=>setAdding(true)} className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5">
            <Plus size={14} /> Add item
          </button>
        </div>
      </div>

      {/* Category tabs */}
      <div className="inline-flex rounded-md border border-[color:var(--aii-border)] overflow-hidden">
        {CATS.map(({ key, label, icon: Icon }) => (
          <button key={key} data-testid={`inv-tab-${key}`} onClick={()=>setCat(key)}
            className={`px-4 py-2 text-sm font-semibold flex items-center gap-2 ${cat===key?"bg-brand text-white":"bg-white"}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* Stats + search */}
      <div className="grid sm:grid-cols-4 gap-3">
        <div className="aii-card p-4">
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Total items</div>
          <div className="text-2xl font-display font-semibold text-brand mt-1">{rows.length}</div>
        </div>
        <div className="aii-card p-4">
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Total units</div>
          <div className="text-2xl font-display font-semibold text-brand mt-1">{totalUnits}</div>
        </div>
        <div className={`aii-card p-4 ${lowStock.length > 0 ? "border-red-300 bg-red-50" : ""}`} data-testid="inv-lowstock-card">
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)] flex items-center gap-1"><AlertTriangle size={11}/> Low stock</div>
          <div className={`text-2xl font-display font-semibold mt-1 ${lowStock.length > 0 ? "text-red-700" : "text-brand"}`}>{lowStock.length}</div>
          <div className="text-[11px] text-[color:var(--aii-muted)] mt-1">below threshold</div>
        </div>
        <div className={`aii-card p-4 ${flagged > 0 ? "border-amber-300 bg-amber-50" : ""}`}>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Needs attention</div>
          <div className={`text-2xl font-display font-semibold mt-1 ${flagged > 0 ? "text-amber-700" : "text-brand"}`}>{flagged}</div>
          <div className="text-[11px] text-[color:var(--aii-muted)] mt-1">repair / replace</div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Search size={14} className="text-[color:var(--aii-muted)]" />
        <input data-testid="inv-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search items…"
          className="flex-1 max-w-md px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" />
      </div>

      {adding && (
        <div className="aii-card p-5" data-testid="inv-form">
          <h3 className="font-semibold mb-3">Add item to {CATS.find(c=>c.key===cat).label}</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <input data-testid="inv-name" placeholder="Item name" value={form.name} onChange={e=>setForm(f=>({...f, name:e.target.value}))}
              className="px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" />
            <input data-testid="inv-units" type="number" placeholder="Units" value={form.units} onChange={e=>setForm(f=>({...f, units:e.target.value}))}
              className="px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" />
            <select data-testid="inv-condition" value={form.condition} onChange={e=>setForm(f=>({...f, condition:e.target.value}))}
              className="px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
              {CONDITIONS.map(c=><option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={()=>setAdding(false)} className="px-4 py-2 text-sm">Cancel</button>
            <button data-testid="inv-save" onClick={add} className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark">Save</button>
          </div>
        </div>
      )}

      <div className="aii-card overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="aii-table">
            <thead><tr><th>Item</th><th>Units</th><th>Threshold</th><th>Condition</th><th>Notes</th><th></th></tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[color:var(--aii-muted)]">{q ? "No items match your search." : "No inventory yet."}</td></tr>}
              {filtered.map(r => {
                const c = CONDITIONS.find(x=>x.v===(r.condition||"good"));
                const low = Number(r.units || 0) < Number(r.threshold || 2);
                return (
                  <tr key={r.id} data-testid={`inv-row-${r.id}`} className={low ? "bg-red-50/40" : ""}>
                    <td className="font-medium">
                      <div className="flex items-center gap-1.5">
                        {low && <AlertTriangle size={13} className="text-red-600" />}
                        {r.name}
                      </div>
                    </td>
                    <td>
                      <input data-testid={`inv-units-${r.id}`} type="number" defaultValue={r.units||0}
                        onBlur={e=>patch(r.id, { units: parseInt(e.target.value) || 0 })}
                        className={`w-20 px-2 py-1 border rounded bg-white text-sm font-semibold ${low ? "border-red-400 text-red-700" : "border-[color:var(--aii-border)]"}`} />
                    </td>
                    <td>
                      <input data-testid={`inv-thr-${r.id}`} type="number" defaultValue={r.threshold ?? 2}
                        onBlur={e=>patch(r.id, { threshold: parseInt(e.target.value) || 0 })}
                        className="w-16 px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm" />
                    </td>
                    <td>
                      <select data-testid={`inv-cond-${r.id}`} value={r.condition||"good"} onChange={e=>patch(r.id, { condition: e.target.value })}
                        className={`px-2 py-1 border rounded bg-white text-sm font-semibold ${c?.cls || ""}`}>
                        {CONDITIONS.map(x=><option key={x.v} value={x.v}>{x.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <input data-testid={`inv-notes-${r.id}`} defaultValue={r.notes||""} placeholder="—"
                        onBlur={e=>patch(r.id, { notes: e.target.value })}
                        className="w-56 px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm" />
                    </td>
                    <td className="text-right">
                      <button data-testid={`inv-del-${r.id}`} onClick={()=>del(r)} className="text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={14}/></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
